// [NEW FILE] Requirement 7: Appraisal page.
//
// After HR finalises a rating, every employee goes one of two ways (backend GET /appraisals):
// - Moved to PIP: the final rating is in the PIP bands set under Scoring & ratings ("Automatic improvement plan"),
//   or the employee already has an open improvement plan. They are handled under Improvement plans (PIP).
// - Applicable for appraisal: every other finalised employee. They are listed here.
// When the PIP rule is "Never", nobody is moved to a PIP and every finalised employee is applicable for the appraisal.
//
// HR / Admin see everybody in the cycle, managers see the people they review, employees see their own decision
// (only after their result is released).
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiAward, FiFlag, FiClock, FiSlash, FiArrowRight, FiInfo } from "react-icons/fi";
import { useCycle } from "../../context/CycleContext";
import { useRole } from "../../context/RoleContext";
import useAsync from "../../hooks/useAsync";
import {
  Card, PageHeader, Banner, DataTable, StatTile, Tabs, Loader, ErrorState, EmptyState, SearchInput, MultiSelect, ProgressBar, Button,
} from "../../components/common";
import { PersonCell } from "../../components/common/Avatar";
import RatingPill from "../../components/performance/RatingPill";
import { getAppraisals, APPRAISAL_DECISION as D, DECISION_TONE, STAGE_LABEL } from "../../services/appraisalApi";
import { VIEWS } from "../../services/navigation";
import { formatDate, formatDateTime, formatScore } from "../../utils/format";

const DecisionBadge = ({ row }) => (
  <span className={`badge badge-${DECISION_TONE[row.decision] || "neutral"}`}>
    <span className="badge-dot" />{row.decisionLabel}
  </span>
);

const ruleText = (rule) => {
  if (!rule.known) return "A final rating in the PIP bands (set by HR under Scoring & ratings) or an open improvement plan moves the employee to a PIP. Every other finalised employee is applicable for the appraisal.";
  return rule.enabled
    ? `Final rating ${rule.pipRatings.map((r) => `"${r}"`).join(" or ")} moves the employee to a PIP. Every other finalised employee is applicable for the appraisal.`
    : "Automatic PIP is set to Never, so no employee is moved to a PIP: every finalised employee is applicable for the appraisal.";
};

function MyAppraisal({ data, cycleName }) {
  const navigate = useNavigate();
  const me = data.rows[0];
  if (!me) {
    return (
      <Card>
        <EmptyState icon={FiInfo} title="You are not part of this cycle" text="Your appraisal appears here once HR adds you to the cycle." />
      </Card>
    );
  }
  const messages = {
    [D.APPRAISAL]: { tone: "success", title: "You are applicable for the appraisal", text: "Your final rating is released and you are not on an improvement plan, so you go forward to the appraisal." },
    [D.PIP]: { tone: "warning", title: "You are on an improvement plan", text: "Your final rating starts an improvement plan (PIP) instead of the appraisal. Open your plan to see the objectives and support." },
    [D.AWAITING_RATING]: { tone: "info", title: "Your rating is not final yet", text: "The appraisal decision is made after HR finalises and releases your rating." },
    [D.AWAITING_RELEASE]: { tone: "info", title: "Your result is not released yet", text: "HR has finalised your review. You see the appraisal decision once the result is released to you." },
    [D.NOT_RATED]: { tone: "neutral", title: "Not rated this cycle", text: "There was no valid performance data to rate, so there is no appraisal decision for this cycle." },
    [D.NOT_ELIGIBLE]: { tone: "neutral", title: "Not eligible this cycle", text: "You were excluded from this cycle. Contact HR if you think this is wrong." },
  };
  const msg = messages[me.decision] || messages[D.AWAITING_RATING];
  return (
    <>
      <Banner
        tone={msg.tone}
        title={msg.title}
        action={me.decision === D.PIP
          ? <Button size="sm" icon={FiFlag} onClick={() => navigate("/pip")}>Open my plan</Button>
          : <Button size="sm" variant="secondary" icon={FiAward} onClick={() => navigate("/my-results")}>My results</Button>}
      >
        {msg.text}
      </Banner>
      <div className="stat-grid">
        <StatTile icon={FiAward} tone={DECISION_TONE[me.decision]} label="Appraisal" value={me.decisionLabel} hint={cycleName} />
        <StatTile icon={FiAward} tone="info" label="Final score" value={me.finalScore === null ? "—" : formatScore(me.finalScore)} hint="Out of 100" />
        <StatTile icon={FiAward} tone="warning" label="Final rating" value={me.rating || "Not yet rated"} hint={me.releasedAt ? `Released ${formatDate(me.releasedAt)}` : "Appears after release"} />
        <StatTile icon={FiClock} tone="neutral" label="Result" value={me.resultLabel} hint={me.recordStatus === "ACKNOWLEDGED" ? "You acknowledged it" : "Acknowledge it under My results"} />
      </div>
      <Card title="How the appraisal decision is made">
        <ul className="icon-list">
          <li>HR finalises your rating after the self assessment and your manager&apos;s assessment.</li>
          <li>{ruleText(data.rule)}</li>
          <li>The decision is shown to you when the result is released.</li>
        </ul>
      </Card>
    </>
  );
}

export default function AppraisalPage() {
  const { cycleId, cycle } = useCycle();
  const { activeView, profile } = useRole();
  const navigate = useNavigate();
  const isEmployee = activeView === VIEWS.EMPLOYEE;
  const isManager = activeView === VIEWS.MANAGER;
  const isHr = !isEmployee && !isManager;
  const { data, error, reload, loading } = useAsync(() => getAppraisals(cycleId), [cycleId], { skip: !cycleId });

  const [tab, setTab] = useState(D.APPRAISAL);
  const [search, setSearch] = useState("");
  const [departments, setDepartments] = useState([]);
  const [ratings, setRatings] = useState([]);

  const rows = useMemo(() => (data ? data.rows : []), [data]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => (tab === "ALL" || r.decision === tab)
      && (!departments.length || departments.includes(r.departmentId))
      && (!ratings.length || ratings.includes(r.rating || ""))
      && (!q || `${r.employeeName} ${r.employeeId} ${r.designationName} ${r.managerName}`.toLowerCase().includes(q)));
  }, [rows, tab, search, departments, ratings]);

  if (!cycleId) return <div className="page"><PageHeader title="Appraisal" /><Card><EmptyState title="No cycle selected" text="Choose a performance cycle at the top." /></Card></div>;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data || (loading && !data)) return <Loader />;

  if (isEmployee) {
    // HR people and managers also see other people's rows; "My appraisal" shows only the signed-in employee.
    const mine = { ...data, rows: rows.filter((r) => r.employeeId === profile?.id) };
    return (
      <div className="page">
        <PageHeader title="My appraisal" subtitle={`${cycle?.name || data.cycleName}. Whether you go forward to the appraisal or to an improvement plan.`} />
        <MyAppraisal data={mine} cycleName={cycle?.name || data.cycleName} />
      </div>
    );
  }

  const count = (k) => rows.filter((r) => r.decision === k).length;
  const applicable = rows.filter((r) => r.decision === D.APPRAISAL);
  const deptOptions = [...new Map(rows.filter((r) => r.departmentId).map((r) => [r.departmentId, { value: r.departmentId, label: r.departmentName }])).values()];
  const ratingOptions = [...new Set(rows.map((r) => r.rating).filter(Boolean))].map((r) => ({ value: r, label: r }));
  const distribution = [...applicable.reduce((m, r) => m.set(r.rating || "—", (m.get(r.rating || "—") || 0) + 1), new Map())]
    .sort((a, b) => b[1] - a[1]);
  const openRow = (r) => {
    if (r.decision === D.PIP && r.pipPlanId) navigate(`/pip?plan=${r.pipPlanId}`);
    else if (isHr) navigate(`/hr/review/${r.employeeId}`);
    else navigate(`/team/${r.employeeId}`);
  };

  return (
    <div className="page">
      <PageHeader
        title={isManager ? "Team appraisal" : "Appraisal"}
        subtitle={`${cycle?.name || data.cycleName}. Employees not moved to a PIP after the final rating are applicable for the appraisal.`}
      />

      <Banner
        tone="info"
        title="How the appraisal list is decided"
        action={isHr && <Link to="/hr/rating-settings" className="btn btn-ghost btn-sm">PIP rule <FiArrowRight size={14} aria-hidden="true" /></Link>}
      >
        {ruleText(data.rule)} Employees are listed once HR has finalised their rating (Review &amp; finalise).
      </Banner>

      <div className="stat-grid">
        <StatTile icon={FiAward} tone="success" label="Applicable for appraisal" value={count(D.APPRAISAL)} hint="Final rating outside the PIP bands" onClick={() => setTab(D.APPRAISAL)} />
        <StatTile icon={FiFlag} tone="danger" label="Moved to PIP" value={count(D.PIP)} hint="Handled under Improvement plans" onClick={() => setTab(D.PIP)} />
        <StatTile icon={FiClock} tone="warning" label="Awaiting final rating" value={count(D.AWAITING_RATING)} hint="Not finalised by HR yet" onClick={() => setTab(D.AWAITING_RATING)} />
        <StatTile icon={FiSlash} tone="neutral" label="Not rated / not eligible" value={count(D.NOT_RATED) + count(D.NOT_ELIGIBLE)} hint="No appraisal decision" onClick={() => setTab("ALL")} />
      </div>

      {/* Appraisal cycle (app/api/cycles.py): status, stage windows, release / acknowledgment and the close rule. */}
      {data.cycle && (
        <div className="grid-2">
          <Card title="Appraisal cycle" subtitle={`${formatDate(data.cycle.performanceStart)} to ${formatDate(data.cycle.performanceEnd)}`}
            actions={isHr && <Link to="/hr/cycles" className="btn btn-ghost btn-sm">Performance cycles <FiArrowRight size={14} aria-hidden="true" /></Link>}>
            <ul className="kv-list">
              <li><span>Status</span><strong>{data.cycle.statusLabel}{data.cycle.launchedAt ? ` (launched ${formatDate(data.cycle.launchedAt)})` : ""}</strong></li>
              {[["GOAL_SETTING", "Goal setting"], ["SELF_ASSESSMENT", "Self assessment"], ["MANAGER_ASSESSMENT", "Manager assessment"], ["HR_FINALIZATION", "HR finalisation"]].map(([k, label]) => {
                const w = data.cycle.windows[k] || [];
                return <li key={k}><span>{label}</span><strong>{w[0] ? `${formatDateTime(w[0])} → ${formatDateTime(w[1])}` : "Not set"}</strong></li>;
              })}
              <li><span>Result release</span><strong>{data.cycle.releaseMode === "ON_FINALIZE" ? "Automatically when HR finalises" : "HR releases manually"}</strong></li>
              <li><span>Employee acknowledgment</span><strong>{data.cycle.acknowledgmentRequired ? "Required before the cycle closes" : "Not required"}</strong></li>
            </ul>
            {isHr && data.cycle.closeBlockers && data.cycle.status !== "CLOSED" && (
              data.cycle.canClose ? (
                <Banner tone="success" title="Ready to close">Every eligible appraisal is {data.cycle.acknowledgmentRequired ? "acknowledged" : "released"}. Close the cycle under Performance cycles.</Banner>
              ) : (
                <Banner tone="warning" title={`${data.cycle.closeBlockers.length} appraisal${data.cycle.closeBlockers.length === 1 ? "" : "s"} still open`}>
                  The cycle can close only when every eligible appraisal is {data.cycle.acknowledgmentRequired ? "acknowledged by the employee" : "released"}.
                  {" "}{data.cycle.closeBlockers.slice(0, 5).map((b) => `${b.name} (${b.stage})`).join(", ")}{data.cycle.closeBlockers.length > 5 ? ` and ${data.cycle.closeBlockers.length - 5} more` : ""}.
                </Banner>
              )
            )}
          </Card>
          <Card title="Where the appraisals are" subtitle="Eligible employees by current stage">
            <ul className="kv-list">
              {Object.keys(STAGE_LABEL).map((k) => {
                const n = data.stages[k] || 0;
                const total = Object.values(data.stages).reduce((a, b) => a + b, 0) || 1;
                return (
                  <li key={k} style={{ display: "block" }}>
                    <span style={{ display: "flex", justifyContent: "space-between" }}><span>{STAGE_LABEL[k]}</span><strong>{n}</strong></span>
                    <ProgressBar value={Math.round((n / total) * 100)} size="sm" tone={k === "RELEASED" ? "success" : "primary"} label={`${STAGE_LABEL[k]} share`} />
                  </li>
                );
              })}
            </ul>
          </Card>
        </div>
      )}

      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { key: D.APPRAISAL, label: "Applicable for appraisal", count: count(D.APPRAISAL) },
          { key: D.PIP, label: "Moved to PIP", count: count(D.PIP) },
          { key: D.AWAITING_RATING, label: "Awaiting rating", count: count(D.AWAITING_RATING) },
          { key: "ALL", label: "All", count: rows.length },
        ]}
      />

      <div className="filter-bar">
        <SearchInput value={search} onChange={setSearch} placeholder="Find an employee, ID or manager" />
        <MultiSelect label="Department" allLabel="All departments" options={deptOptions} value={departments} onChange={setDepartments} />
        <MultiSelect label="Rating" allLabel="Any rating" options={ratingOptions} value={ratings} onChange={setRatings} />
      </div>

      <div className="stack">
        <Card padded={false} title={{ [D.APPRAISAL]: "Applicable for appraisal", [D.PIP]: "Moved to PIP", [D.AWAITING_RATING]: "Awaiting final rating", ALL: "Everyone in the cycle" }[tab]}>
          <DataTable
            label="Appraisal"
            exportName={`appraisal-${cycle?.name || ""}`}
            rows={visible}
            rowKey="id"
            onRowClick={openRow}
            emptyTitle="Nobody here"
            emptyText={tab === D.APPRAISAL ? "Employees appear here once HR finalises a rating outside the PIP bands." : undefined}
            columns={[
              { key: "employeeName", label: "Employee", render: (r) => <PersonCell name={r.employeeName} meta={`${r.employeeId} · ${r.designationName}`} /> },
              { key: "departmentName", label: "Department" },
              { key: "managerName", label: "Reporting manager" },
              { key: "goalScore", label: "Goal score", align: "right", render: (r) => formatScore(r.goalScore), exportValue: (r) => r.goalScore ?? "" },
              { key: "kraScore", label: "KRA score", align: "right", render: (r) => formatScore(r.kraScore), exportValue: (r) => r.kraScore ?? "" },
              { key: "finalScore", label: "Final score", align: "right", render: (r) => <strong>{formatScore(r.finalScore)}</strong>, exportValue: (r) => r.finalScore ?? "" },
              { key: "rating", label: "Rating", render: (r) => <RatingPill rating={r.rating} tone={DECISION_TONE[r.decision]} size="sm" />, exportValue: (r) => r.rating || "" },
              { key: "stageLabel", label: "Stage", render: (r) => (r.eligibility === "ELIGIBLE" ? r.stageLabel : <span title={r.exclusionReason}>Excluded</span>) },
              { key: "resultLabel", label: "Result" },
              { key: "decision", label: "Appraisal", render: (r) => <DecisionBadge row={r} />, exportValue: (r) => r.decisionLabel },
              { key: "act", label: "", align: "right", render: (r) => <Button variant="secondary" size="sm">{r.decision === D.PIP && r.pipPlanId ? "Open plan" : "Open"}</Button> },
            ]}
          />
        </Card>
        <div className="grid-2">
          <Card title="Ratings of the appraisal list" subtitle={`${applicable.length} employee${applicable.length === 1 ? "" : "s"} applicable`}>
            {distribution.length === 0 ? (
              <EmptyState title="No finalised ratings yet" compact />
            ) : (
              <ul className="kv-list">
                {distribution.map(([label, n]) => (
                  <li key={label} style={{ display: "block" }}>
                    <span style={{ display: "flex", justifyContent: "space-between" }}><span>{label}</span><strong>{n}</strong></span>
                    <ProgressBar value={Math.round((n / applicable.length) * 100)} size="sm" tone="success" label={`${label} share`} />
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card title="What this page shows">
            <div className="appraisal-notes">
              <p><strong>Applicable for appraisal</strong>: rating finalised by HR and not in the PIP bands.</p>
              <p><strong>Moved to PIP</strong>: rating in the PIP bands, or an improvement plan is open. Start the draft plan under Improvement plans.</p>
              <p><strong>Awaiting rating</strong>: goals, self assessment, manager assessment or HR finalisation still open.</p>
              <p><strong>Not eligible</strong>: HR excluded the employee from this cycle (for example joined late or exited).</p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
